import { useState, useEffect } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { BrandLogo } from '../components/BrandLogo'

interface InviteData {
  email: string
  name: string
  role: string
  orgName: string
  inviterName: string
  isExistingUser: boolean
  existingUserName?: string
}

type InviteStatus = 'loading' | 'valid' | 'used' | 'expired' | 'invalid' | 'error'

export function AcceptInvitePage() {
  const search = useSearch({ strict: false }) as { token?: string }
  const token = search.token
  const setAuth = useAuthStore((s) => s.setAuth)
  
  const [status, setStatus] = useState<InviteStatus>('loading')
  const [inviteData, setInviteData] = useState<InviteData | null>(null)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isComplete, setIsComplete] = useState(false)

  // Redirect to dashboard after successful signup
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(() => {
        window.location.href = '/dashboard'
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [isComplete])

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }

    const validateToken = async () => {
      try {
        const res = await fetch(`/api/v1/auth/invite/${token}`)
        const data = await res.json()
        
        if (res.ok) {
          setInviteData(data)
          // For new users, use the suggested name from invite
          // For existing users, use their existing name
          setName(data.isExistingUser ? data.existingUserName : data.name)
          setStatus('valid')
        } else if (data.code === 'TOKEN_USED') {
          setStatus('used')
        } else if (data.code === 'TOKEN_EXPIRED') {
          setStatus('expired')
        } else {
          setStatus('invalid')
        }
      } catch {
        setStatus('error')
      }
    }

    validateToken()
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    // Only check password confirmation for new users
    if (!inviteData?.isExistingUser && password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    // Password length check only for new users creating a password
    if (!inviteData?.isExistingUser && password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      const res = await fetch('/api/v1/auth/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token, 
          name: inviteData?.isExistingUser ? undefined : name, // Only send name for new users
          password,
          isExistingUser: inviteData?.isExistingUser,
        }),
      })
      
      const data = await res.json()
      
      if (!res.ok) {
        if (data.code === 'TOKEN_EXPIRED') {
          setStatus('expired')
        } else if (data.code === 'TOKEN_USED') {
          setStatus('used')
        } else if (data.code === 'INVALID_PASSWORD') {
          setError('Incorrect password. Please enter your Roomsi password.')
        } else {
          setError(data.error || 'Failed to accept invitation')
        }
        return
      }
      
      // Success! Update auth state and show success animation
      setIsComplete(true)
      
      // If there's a pending booking from the original access request, store it for the dashboard
      if (data.pendingBooking && data.pendingBooking.roomId) {
        localStorage.setItem('pendingBooking', JSON.stringify({
          roomId: data.pendingBooking.roomId,
          preferredDate: data.pendingBooking.preferredDate,
          preferredTime: data.pendingBooking.preferredTime,
          preferredSlots: data.pendingBooking.preferredSlots,  // Raw 24h format: "14:00,15:00,18:00"
          message: data.pendingBooking.message,
          orgId: data.organisation.id,
        }))
      }
      
      // Set auth state
      setAuth({
        user: data.user,
        memberships: [{
          orgId: data.organisation.id,
          orgSlug: data.organisation.slug,
          orgName: data.organisation.name,
          role: inviteData?.role || 'guest',
          accountType: 'guest',
          status: 'active',
          attributes: {},
        }],
        currentOrg: {
          orgId: data.organisation.id,
          orgSlug: data.organisation.slug,
          role: inviteData?.role || 'guest',
        },
        accessToken: data.accessToken,
      })
      
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Error states
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Validating your invitation...</p>
        </div>
      </div>
    )
  }

  if (status === 'invalid' || status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Invalid Invitation</h1>
            <p className="mt-2 text-gray-600">
              This invitation link is invalid or has been corrupted.
            </p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'used') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <CheckCircle className="w-16 h-16 text-amber-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Already Accepted</h1>
            <p className="mt-2 text-gray-600">
              This invitation has already been used. If this was you, you can log in below.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Log In
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <XCircle className="w-16 h-16 text-orange-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Invitation Expired</h1>
            <p className="mt-2 text-gray-600">
              This invitation has expired. Please ask the person who invited you to send a new one.
            </p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Success animation
  if (isComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 animate-in fade-in zoom-in duration-500">
            <BrandLogo size="lg" />
            <h1 className="mt-4 text-3xl font-bold text-gray-900">Welcome to Roomsi!</h1>
            <p className="mt-2 text-lg text-gray-600">
              Your account has been created successfully.
            </p>
            <p className="mt-4 text-gray-500">
              Redirecting to dashboard...
            </p>
            <Loader2 className="w-6 h-6 animate-spin text-primary-600 mx-auto mt-4" />
          </div>
        </div>
      </div>
    )
  }

  // Main form
  return (
    <div className="auth-page min-h-screen flex items-center justify-center bg-background py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <BrandLogo size="lg" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            You're invited
          </h1>
          <p className="mt-2 text-gray-600">
            <span className="font-medium text-primary-600">{inviteData?.inviterName}</span> invited you to join{' '}
            <span className="font-medium text-gray-900">{inviteData?.orgName}</span>
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border border-border rounded-2xl overflow-hidden">
          {/* Role badge */}
          <div className="bg-community-soft border-b border-border px-6 py-4 text-center">
            <p className="text-gray-600 text-sm">You'll be joining as</p>
            <p className="text-community-strong text-xl font-semibold capitalize">{inviteData?.role}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={inviteData?.email || ''}
                disabled
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>

            {inviteData?.isExistingUser ? (
              <>
                {/* Name (read-only for existing users) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={inviteData.existingUserName || ''}
                    disabled
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* Info message for existing users */}
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
                  <p className="font-medium">Welcome back!</p>
                  <p className="mt-1">You already have a Roomsi account. Enter your existing password to join this organisation.</p>
                </div>

                {/* Password (verify existing) */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Roomsi Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                      placeholder="Enter your existing password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Name (editable for new users) */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    minLength={2}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                    placeholder="Your full name"
                  />
                </div>

                {/* Password (create new) */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Create Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full px-4 py-2.5 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                      placeholder="At least 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password (new users only) */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
                    placeholder="Confirm your password"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-sm text-red-500">Passwords don't match</p>
                  )}
                </div>
              </>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || (!inviteData?.isExistingUser && password !== confirmPassword)}
              className="w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {inviteData?.isExistingUser ? 'Joining Organisation...' : 'Creating Account...'}
                </>
              ) : (
                inviteData?.isExistingUser ? 'Join Organisation' : 'Create Account & Join'
              )}
            </button>

            {!inviteData?.isExistingUser && (
              <p className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-600 hover:underline">
                  Log in
                </Link>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}
